/**
 * SearchBar Component - Advanced search with debouncing, caching, and autocomplete
 * 
 * Main component providing comprehensive search with caching, autocomplete,
 * keyboard navigation, and accessibility features.
 */

import React, {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { useSearchCache } from '../hooks/useSearchCache';
import { useRecentSearches } from '../hooks/useRecentSearches';
import { SearchBarProps } from '../types/search';
import {
  useSearchBarState,
  useSearchBarKeyboard,
  useSearchBarFocus,
  useSuggestionSelection,
} from './SearchBar/SearchBarHooks';
import {
  SearchBarHelpers
} from './SearchBar/SearchBarCore';
import { SearchDropdown } from './SearchDropdown';
import { searchBarStyles } from './SearchBar.styles';
import { SearchBarInput } from './SearchBar/SearchBarInput';
import {
  SearchBarOptimizations
} from './SearchBar/SearchBarOptimizations';
import {
  useSearchLogic,
  useInputChangeHandler,
  useClearHandler,
  useSuggestionHoverHandler,
  useAutoFocus
} from './SearchBar/useSearchBarHelpers';


/**
 * Main SearchBar component
 */
export const SearchBar: React.FC<SearchBarProps> = React.memo(({
  placeholder = 'Search...',
  onSearch,
  onSelect,
  debounceMs = 300,
  maxSuggestions = 10,
  showRecentSearches = true,
  cacheConfig,
  className = '',
  disabled = false,
  autoFocus = false,
  clearOnSelect = false,
}) => {
  // Initialize component state and refs
  const { state, setState, safeSetState, mountedRef } = useSearchBarState({
    query: '', isLoading: false, isOpen: false, results: [], suggestions: [], error: null, selectedIndex: -1,
  });

  // Refs for DOM elements
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Custom hooks
  const debouncedQuery = useDebounce(state.query, debounceMs);
  const cache = useSearchCache(cacheConfig);
  const { recentSearches, addSearch } = useRecentSearches();

  // Component behavior hooks
  const { handleSuggestionSelect } = useSuggestionSelection(onSelect, addSearch, clearOnSelect);
  const { handleKeyboardNavigation, handleKeyDown } = useSearchBarKeyboard(state, setState, handleSuggestionSelect);
  const { handleInputFocus, handleInputBlur } = useSearchBarFocus(setState, mountedRef);

  // Custom hooks for handlers
  const { handleInputChange } = useInputChangeHandler(setState);
  const { performSearch } = useSearchLogic(
    onSearch, cache, recentSearches, maxSuggestions, showRecentSearches, safeSetState
  );
  const { handleClearClick } = useClearHandler(handleKeyboardNavigation, inputRef);
  const { handleSuggestionHover } = useSuggestionHoverHandler(setState);

  // Effects
  useEffect(() => {
    if (debouncedQuery !== state.query) return;
    performSearch(debouncedQuery);
  }, [debouncedQuery, performSearch, state.query]);

  useEffect(() => {
    SearchBarHelpers.scrollSelectedIntoView(state.selectedIndex, listRef);
  }, [state.selectedIndex]);

  // Auto-focus effect
  useAutoFocus(autoFocus, inputRef);

  // Dropdown visibility and component IDs
  const { showDropdown, componentId, dropdownId, inputId } = useMemo(() => 
    SearchBarOptimizations.getDisplayConfiguration(state),
    [state]
  );

  // ARIA attributes and visibility states
  const { ariaExpanded, ariaActivedescendant, ariaDescribedby } = SearchBarOptimizations.getAriaDescriptors(state, componentId);
  const { shouldShowClearButton, shouldShowLoadingSpinner, shouldShowSearchIcon } = SearchBarOptimizations.getVisibilityStates(state);
  const screenReaderText = SearchBarOptimizations.getScreenReaderAnnouncement(state);

  return (
    <div className={`${searchBarStyles.container} ${className}`}>
      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={searchBarStyles.srOnly}
      >
        {screenReaderText}
      </div>

      {/* Search input with buttons */}
      <SearchBarInput
        inputRef={inputRef}
        inputId={inputId}
        state={state}
        placeholder={placeholder}
        disabled={disabled}
        ariaExpanded={Boolean(ariaExpanded)}
        dropdownId={dropdownId}
        ariaActivedescendant={ariaActivedescendant}
        ariaDescribedby={ariaDescribedby}
        handleInputChange={handleInputChange}
        handleInputFocus={handleInputFocus}
        handleInputBlur={handleInputBlur}
        handleKeyDown={handleKeyDown}
        handleClearClick={handleClearClick}
        shouldShowClearButton={shouldShowClearButton}
        shouldShowLoadingSpinner={shouldShowLoadingSpinner}
        shouldShowSearchIcon={shouldShowSearchIcon}
      />

      {/* Dropdown with suggestions */}
      <SearchDropdown
        state={state}
        componentId={componentId}
        dropdownId={dropdownId}
        showDropdown={showDropdown}
        onSuggestionClick={(suggestion) => {
          const searchResult = SearchBarOptimizations.createSearchResult(suggestion);
          setState(handleSuggestionSelect(searchResult));
        }}
        onSuggestionHover={handleSuggestionHover}
        listRef={listRef}
        dropdownRef={dropdownRef}
      />
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

export default SearchBar;